import { useCallback, useEffect, useRef, useState } from 'react';
import type { components } from '@/lib/api-types-administration';

export type PlaceSummary = components['schemas']['PlaceSummary'];
type PlacePage = components['schemas']['PlacePage'];

export type PlacePageState =
  | { readonly status: 'loading'; readonly items: readonly PlaceSummary[] }
  | {
      readonly status: 'ready';
      readonly items: readonly PlaceSummary[];
      readonly total: number;
      readonly hasMore: boolean;
    }
  | { readonly status: 'error'; readonly items: readonly PlaceSummary[] };

export type PageRead = (query: {
  q?: string;
  cursor?: string;
}) => Promise<{ data?: PlacePage; response: Response }>;

/**
 * One of the chooser's paged lists — a tenant's organizations, or the platform's tenants — with
 * the search the server does (`q`: name contains, ignoring case and accents) and «Mostra altri»
 * through the opaque cursor. A new search starts again from the first page. A refused cursor
 * (400 INVALID_CURSOR) is treated like any failure: the list says so and offers to retry, which
 * starts from the first page.
 */
export function usePlacePage(
  read: PageRead | null,
  q: string,
): {
  readonly state: PlacePageState;
  readonly more: () => void;
  readonly retry: () => void;
} {
  const [state, setState] = useState<PlacePageState>({ status: 'loading', items: [] });
  const [cursor, setCursor] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Every first page starts a new generation: a «Mostra altri» page asked for an earlier search is
  // dropped when it answers, instead of mixing places from two searches.
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    if (read === null) {
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', items: [] });
    setCursor(null);
    void read(q === '' ? {} : { q })
      .then(({ data, response }) => {
        if (cancelled) {
          return;
        }
        if (!response.ok || data === undefined) {
          setState({ status: 'error', items: [] });
          return;
        }
        setCursor(data.nextCursor);
        setState({
          status: 'ready',
          items: data.items,
          total: data.total,
          hasMore: data.nextCursor !== null,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: 'error', items: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [read, q, attempt]);

  const more = useCallback(() => {
    if (read === null || cursor === null || state.status !== 'ready') {
      return;
    }
    const before = state.items;
    const asked = generation.current;
    setState({ status: 'loading', items: before });
    void read(q === '' ? { cursor } : { q, cursor })
      .then(({ data, response }) => {
        if (generation.current !== asked) {
          return;
        }
        if (!response.ok || data === undefined) {
          setState({ status: 'error', items: before });
          return;
        }
        setCursor(data.nextCursor);
        setState({
          status: 'ready',
          items: [...before, ...data.items],
          total: data.total,
          hasMore: data.nextCursor !== null,
        });
      })
      .catch(() => {
        if (generation.current === asked) {
          setState({ status: 'error', items: before });
        }
      });
  }, [read, cursor, q, state]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { state, more, retry };
}
